// frontend/src/hooks/useApi.ts - PROPERLY REFACTORED CORE UTILITIES ONLY
// FIXED: Added missing React imports and removed entity-specific hooks that belong in separate files
// This file now contains ONLY core utilities that other hooks depend on

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuthStore } from '@/store/auth';

// Import only types from API - not functions
import type {
  // Response types
  StandardResponse,
  PaginatedResponse,
  ApiErrorResponse,
  
  // Query parameter types
  QueryParams
} from '@/api/types';

// ===== CORE INTERFACES =====

export interface UseApiOptions {
  immediate?: boolean;
  dependencies?: any[];
  retryCount?: number;
  retryDelay?: number;
  rateLimitKey?: string;
  debounce?: number;
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
}

export interface UseMutationOptions<TResult = any, TVariables = any> {
  onSuccess?: (data: TResult, variables: TVariables) => void;
  onError?: (error: any, variables: TVariables) => void;
}

// ===== UTILITY FUNCTIONS =====

/**
 * MongoDB ObjectId validation utility
 */
export const validateObjectId = (id: string | undefined | null, fieldName: string = 'ID'): void => {
  if (!id) {
    throw new Error(`${fieldName} is required`);
  }
  
  if (typeof id !== 'string') {
    throw new Error(`${fieldName} must be a string`);
  }
  
  // MongoDB ObjectId format: 24 character hex string
  const objectIdRegex = /^[0-9a-fA-F]{24}$/;
  if (!objectIdRegex.test(id)) {
    throw new Error(`Invalid ${fieldName} format. Expected 24-character MongoDB ObjectId, received: ${id}`);
  }
};

/**
 * Validate array of ObjectIds
 */
export const validateObjectIds = (ids: string[], fieldName: string = 'IDs'): void => {
  if (!Array.isArray(ids)) {
    throw new Error(`${fieldName} must be an array`);
  }
  
  if (ids.length === 0) {
    throw new Error(`${fieldName} array cannot be empty`);
  }
  
  ids.forEach((id, index) => {
    validateObjectId(id, `${fieldName}[${index}]`);
  });
};

/**
 * Response normalization utilities
 */
export const normalizeSingleResponse = <T = any>(response: StandardResponse<T> | T): T => {
  // If response has data property (StandardResponse), extract it
  if (response && typeof response === 'object' && 'data' in response) {
    const standardResponse = response as StandardResponse<T>;
    if (standardResponse.data !== undefined) {
      return standardResponse.data;
    }
    // If data is undefined, return the response itself (might be direct data)
    return response as T;
  }
  
  // Otherwise assume it's already the data
  return response as T;
};

export const normalizePaginatedResponse = <T = any>(response: PaginatedResponse<T>): PaginatedResponse<T> => {
  // Ensure response has all required fields with defaults
  return {
    data: response.data || [],
    pagination: response.pagination || {
      currentPage: 1,
      totalPages: 0,
      totalItems: 0,
      itemsPerPage: 10,
      hasNextPage: false,
      hasPrevPage: false
    },
    total: response.total || 0,
    totalPages: response.totalPages || 0,
    page: response.page || 1,
    limit: response.limit || 10
  };
};

// ===== CORE HOOKS =====

/**
 * FIXED: Base useApi hook with stable dependencies to prevent infinite loops
 */
export function useApi<TResult = any>(
  apiFn: () => Promise<TResult>,
  options: UseApiOptions = {}
): {
  data: TResult | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
} {
  const [data, setData] = useState<TResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const loadingRef = useRef(false);
  const hasExecutedRef = useRef(false);
  const { updateActivity } = useAuthStore();

  // A refetch, or a change of parameters, requested while a request is still
  // in flight used to be DROPPED: execute() returned early and nothing ever
  // ran the newer request. The screen then stayed on the older answer — e.g.
  // after adding a group member the member lists froze on the previous
  // search, the new member still looked "available", and people added them
  // again (customer report, 2026-09-28). Now such a request is remembered and
  // run as soon as the current one finishes, with the latest parameters.
  const pendingRef = useRef(false);
  const mountedRef = useRef(true);
  const latestExecuteRef = useRef<(() => Promise<void>) | null>(null);

  // FIXED: Use refs to prevent infinite loops and stabilize callbacks
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const execute = useCallback(async () => {
    // Use ref to check loading state instead of state variable
    if (loadingRef.current) return;

    try {
      abortControllerRef.current = new AbortController();
      
      loadingRef.current = true;
      setLoading(true);
      setError(null);
      
      let attempt = 0;
      const maxRetries = optionsRef.current.retryCount ?? 1;
      
      while (attempt <= maxRetries) {
        try {
          const result = await apiFn();
          
          // CRITICAL FIX: Ensure data is properly set
          setData(result);
          loadingRef.current = false;
          setLoading(false);
          hasExecutedRef.current = true;
          
          updateActivity?.();
          
          if (optionsRef.current.onSuccess) {
            optionsRef.current.onSuccess(result);
          }
          
          return;
          
        } catch (err: any) {
          attempt++;
          
          if (attempt > maxRetries) {
            const errorMessage = err?.message || 'API call failed';
            console.error('useApi: Setting error:', errorMessage);
            setError(errorMessage);
            loadingRef.current = false;
            setLoading(false);
            hasExecutedRef.current = true;
            
            if (optionsRef.current.onError) {
              optionsRef.current.onError(err);
            }
            return;
          }
          
          // Wait before retrying
          if (attempt <= maxRetries) {
            await new Promise(resolve => 
              setTimeout(resolve, optionsRef.current.retryDelay ?? 1000)
            );
          }
        }
      }
    } catch (err: any) {
      console.error('useApi: Unexpected error:', err);
      setError(err?.message || 'Unexpected error occurred');
      loadingRef.current = false;
      setLoading(false);
      hasExecutedRef.current = true;
      
      if (optionsRef.current.onError) {
        optionsRef.current.onError(err);
      }
    } finally {
      // Run the request that arrived while this one was in flight. Deferred a
      // tick so it picks up the newest parameters (latestExecuteRef is
      // refreshed on every render).
      if (pendingRef.current && mountedRef.current) {
        pendingRef.current = false;
        hasExecutedRef.current = false;
        setTimeout(() => { latestExecuteRef.current?.(); }, 0);
      }
    }
  }, [apiFn, updateActivity]);
  latestExecuteRef.current = execute;

  // FIXED: Stable dependency array to prevent infinite loops
  const depsString = JSON.stringify(options.dependencies || []);
  const prevDepsRef = useRef<string>(depsString);

  useEffect(() => {
    const shouldExecute = optionsRef.current.immediate ?? false;

    // Only reset execution flag if dependencies actually changed
    const depsChanged = prevDepsRef.current !== depsString;
    if (depsChanged) {
      prevDepsRef.current = depsString;
      hasExecutedRef.current = false;
      // New parameters while the old request is still running: run the new
      // one when it finishes instead of losing it.
      if (loadingRef.current) pendingRef.current = true;
    }


    if (shouldExecute && !hasExecutedRef.current) {
      if (optionsRef.current.debounce && optionsRef.current.debounce > 0) {
        const timeoutId = setTimeout(execute, optionsRef.current.debounce);
        return () => clearTimeout(timeoutId);
      } else {
        execute();
      }
    }
  }, [
    depsString, // Only stable dependencies
    execute
  ]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const refetch = useCallback(async () => {
    hasExecutedRef.current = false; // Reset flag for manual refetch
    if (loadingRef.current) {
      // A request is already running and may have started before whatever
      // prompted this refetch (a save). Run again once it finishes.
      pendingRef.current = true;
      return;
    }
    await execute();
  }, [execute]);

  return {
    data,
    loading,
    error,
    refetch
  };
}

/**
 * Mutation hook for API operations - FIXED with stable dependencies
 */
export function useMutation<TResult = any, TVariables = any>(
  mutationFn: (variables: TVariables) => Promise<TResult>,
  options: UseMutationOptions<TResult, TVariables> = {}
): {
  mutate: (variables: TVariables) => Promise<TResult>;
  loading: boolean;
  error: string | null;
  reset: () => void;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { updateActivity } = useAuthStore();

  // FIXED: Memoize options to prevent instability
  const memoizedOptions = useMemo(() => options, [options.onSuccess, options.onError]);

  const mutate = useCallback(async (variables: TVariables): Promise<TResult> => {
    setLoading(true);
    setError(null);

    try {
      
      const result = await mutationFn(variables);
      
      updateActivity();
      memoizedOptions.onSuccess?.(result, variables);
      
      return result;
    } catch (err: any) {
      const errorMessage = err.message || 'Mutation failed';
      setError(errorMessage);
      memoizedOptions.onError?.(err, variables);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [mutationFn, memoizedOptions, updateActivity]);

  const reset = useCallback(() => {
    setLoading(false);
    setError(null);
  }, []);

  return { mutate, loading, error, reset };
}

// ===== GENERIC BATCH OPERATIONS HOOK =====

/**
 * Generic batch operation hook (not entity-specific)
 */
export const useBatchOperation = (options?: UseMutationOptions) => {
  return useMutation(
    async (batchData: { operation: string; entities: string[]; params?: any }) => {
      
      // Generic batch operation logic
      const { operation, entities, params } = batchData;
      
      switch (operation) {
        case 'delete':
          validateObjectIds(entities, 'Entity IDs');
          break;
        case 'update':
          validateObjectIds(entities, 'Entity IDs');
          if (!params) {
            throw new Error('Update operation requires params');
          }
          break;
        default:
          throw new Error(`Unsupported batch operation: ${operation}`);
      }
      
      return { 
        operation, 
        processedCount: entities.length, 
        entities, 
        success: true 
      };
    },
    options
  );
};

// ===== UTILITY HOOKS =====

/**
 * API cache hook for caching API responses
 */
export const useApiCache = () => {
  const [cache] = useState(new Map());

  const getCached = useCallback((key: string) => {
    const item = cache.get(key);
    if (!item) return null;
    
    // Check if item has expired
    if (item.ttl && Date.now() - item.timestamp > item.ttl) {
      cache.delete(key);
      return null;
    }
    
    return item.value;
  }, [cache]);

  const setCached = useCallback((key: string, value: any, ttl?: number) => {
    const item = {
      value,
      timestamp: Date.now(),
      ttl: ttl || 5 * 60 * 1000 // 5 minutes default
    };
    cache.set(key, item);
  }, [cache]);

  const clearCache = useCallback((pattern?: string) => {
    if (pattern) {
      // Clear entries matching pattern
      for (const key of cache.keys()) {
        if (key.includes(pattern)) {
          cache.delete(key);
        }
      }
    } else {
      cache.clear();
    }
  }, [cache]);

  const getCacheInfo = useCallback(() => {
    return {
      size: cache.size,
      keys: Array.from(cache.keys())
    };
  }, [cache]);

  return { getCached, setCached, clearCache, getCacheInfo };
};

/**
 * Health check hook
 */
export const useHealthCheck = (options?: UseApiOptions) => {
  return useApi(
    async () => {
      try {
        // This would call a health check endpoint
        const response = await fetch('/api/health');
        if (!response.ok) {
          throw new Error(`Health check failed: ${response.status}`);
        }
        return await response.json();
      } catch (error) {
        return { 
          status: 'error', 
          message: 'Health check endpoint not available',
          timestamp: new Date().toISOString()
        };
      }
    },
    {
      immediate: false,
      rateLimitKey: 'health_check',
      ...options
    }
  );
};

/**
 * API metrics and performance monitoring
 */
export const useApiMetrics = () => {
  const [metrics, setMetrics] = useState({
    requestCount: 0,
    errorCount: 0,
    averageResponseTime: 0,
    totalResponseTime: 0
  });

  const updateMetrics = useCallback((responseTime: number, isError: boolean = false) => {
    setMetrics(prev => {
      const newRequestCount = prev.requestCount + 1;
      const newTotalResponseTime = prev.totalResponseTime + responseTime;
      
      return {
        requestCount: newRequestCount,
        errorCount: prev.errorCount + (isError ? 1 : 0),
        totalResponseTime: newTotalResponseTime,
        averageResponseTime: newTotalResponseTime / newRequestCount
      };
    });
  }, []);

  const resetMetrics = useCallback(() => {
    setMetrics({
      requestCount: 0,
      errorCount: 0,
      averageResponseTime: 0,
      totalResponseTime: 0
    });
  }, []);

  const getSuccessRate = useCallback(() => {
    if (metrics.requestCount === 0) return 0;
    return ((metrics.requestCount - metrics.errorCount) / metrics.requestCount) * 100;
  }, [metrics]);

  return { 
    metrics, 
    updateMetrics, 
    resetMetrics, 
    getSuccessRate,
    get successRate() { return getSuccessRate(); }
  };
};

/**
 * Error recovery and retry queue management
 */
export const useApiErrorRecovery = () => {
  const [retryQueue, setRetryQueue] = useState<Array<{ 
    id: string;
    fn: Function; 
    attempts: number; 
    maxAttempts: number;
    lastError?: any;
  }>>([]);

  const addToRetryQueue = useCallback((
    fn: Function, 
    maxAttempts: number = 3,
    id?: string
  ) => {
    const retryId = id || `retry_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    setRetryQueue(prev => [...prev, { 
      id: retryId,
      fn, 
      attempts: 0, 
      maxAttempts 
    }]);
    
    return retryId;
  }, []);

  const processRetryQueue = useCallback(async () => {
    
    const updatedQueue = [];
    
    for (const item of retryQueue) {
      try {
        await item.fn();
      } catch (error) {
        console.warn(`Retry failed for item ${item.id}, attempt ${item.attempts + 1}/${item.maxAttempts}`);
        
        if (item.attempts + 1 < item.maxAttempts) {
          updatedQueue.push({ 
            ...item, 
            attempts: item.attempts + 1,
            lastError: error 
          });
        } else {
          console.error(`Max retry attempts reached for item ${item.id}`, error);
        }
      }
    }
    
    setRetryQueue(updatedQueue);
  }, [retryQueue]);

  const removeFromRetryQueue = useCallback((id: string) => {
    setRetryQueue(prev => prev.filter(item => item.id !== id));
  }, []);

  const clearRetryQueue = useCallback(() => {
    setRetryQueue([]);
  }, []);

  const getQueueStatus = useCallback(() => {
    return {
      totalItems: retryQueue.length,
      itemsByAttempts: retryQueue.reduce((acc, item) => {
        acc[item.attempts] = (acc[item.attempts] || 0) + 1;
        return acc;
      }, {} as Record<number, number>)
    };
  }, [retryQueue]);

  return {
    retryQueue,
    addToRetryQueue,
    processRetryQueue,
    removeFromRetryQueue,
    clearRetryQueue,
    getQueueStatus
  };
};

/**
 * Debug utilities for API monitoring
 */
export const useApiDebug = () => {
  const [debugInfo, setDebugInfo] = useState<Record<string, any>>({});
  const [isDebugMode, setIsDebugMode] = useState(false);

  const logApiCall = useCallback((
    endpoint: string, 
    method: string = 'GET',
    params?: any, 
    result?: any,
    responseTime?: number,
    error?: any
  ) => {
    if (isDebugMode) {
      const logEntry = {
        timestamp: new Date().toISOString(),
        endpoint,
        method: method.toUpperCase(),
        params: params ? JSON.stringify(params).slice(0, 200) + (JSON.stringify(params).length > 200 ? '...' : '') : undefined,
        result: result ? JSON.stringify(result).slice(0, 200) + (JSON.stringify(result).length > 200 ? '...' : '') : undefined,
        responseTime,
        error: error ? error.message || error : undefined,
        success: !error
      };

      console.group(`🔍 API Debug: ${method.toUpperCase()} ${endpoint}`);
      if (error) {
        console.error('❌ Error:', error);
      } else {
      }
      console.groupEnd();
      
      setDebugInfo(prev => ({
        ...prev,
        [endpoint]: logEntry
      }));
    }
  }, [isDebugMode]);

  const toggleDebugMode = useCallback(() => {
    setIsDebugMode(prev => {
      const newMode = !prev;
      return newMode;
    });
  }, []);

  const clearDebugInfo = useCallback(() => {
    setDebugInfo({});
  }, []);

  const exportDebugInfo = useCallback(() => {
    return {
      debugMode: isDebugMode,
      entries: Object.values(debugInfo),
      totalCalls: Object.keys(debugInfo).length,
      exportedAt: new Date().toISOString()
    };
  }, [isDebugMode, debugInfo]);

  return {
    debugInfo,
    isDebugMode,
    logApiCall,
    toggleDebugMode,
    clearDebugInfo,
    exportDebugInfo
  };
};

// ===== CONSTANTS =====

export const API_CONSTANTS = {
  DEFAULT_PAGE_SIZE: 10,
  MAX_PAGE_SIZE: 100,
  DEFAULT_RETRY_COUNT: 2,
  DEFAULT_RETRY_DELAY: 1000,
  DEFAULT_DEBOUNCE: 300,
  CACHE_TTL: 300000, // 5 minutes
  RATE_LIMIT_WINDOW: 60000 // 1 minute
} as const;

// ===== EXPORTS =====

// Core hooks and utility functions are already exported above with export keyword
// Types are already exported with their interface declarations
// No need to re-export them here to avoid redeclaration errors

// Default export for backward compatibility - core utilities only
const useApiCore = {
  // Core hooks
  useApi,
  useMutation,
  
  // Generic operations
  useBatchOperation,
  
  // Utility hooks
  useApiCache,
  useHealthCheck,
  useApiMetrics,
  useApiErrorRecovery,
  useApiDebug,
  
  // Utility functions
  validateObjectId,
  validateObjectIds,
  normalizeSingleResponse,
  normalizePaginatedResponse,
  
  // Constants
  API_CONSTANTS
};

export default useApiCore;